import React from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import CallVideoTiles from "./CallVideoTiles";
import TimeWarningModal from "../../../components/TimeWarningModal";
import { CallWebViewProps } from "../call.types";
import MicIcon from "../../../assets/mic-icon";
import CameraIcon from "../../../assets/camera-icon";
import BlurIcon from "../../../assets/blur-icon";
import AdviceIcon from "../../../assets/advice-icon";
import ConversationPanelMobile from "./ConversationPanelMobile";

export default function CallWebViewMobile({
  calling,
  mediaPermissionError,
  isCheckingPermissions,
  isFetchingToken,
  userName,
  remoteUsers,
  remoteNames,
  localMicrophoneTrack,
  localCameraTrack,
  micOn,
  cameraOn,
  isBlurEnabled,
  isConversationPanelOpen,
  isTimeWarningModalVisible,
  onRetryPermissions,
  onToggleMic,
  onToggleCamera,
  onToggleBlur,
  onToggleConversationPanel,
  onCloseConversationPanel,
  onCloseTimeWarning,
  onFlagUser,
}: CallWebViewProps) {
  return (
    <View style={styles.page}>
      {!calling ? (
        <View style={styles.center}>
          {mediaPermissionError ? (
            <>
              <Text style={styles.errorText}>{mediaPermissionError}</Text>
              <TouchableOpacity
                style={styles.retry}
                onPress={onRetryPermissions}
                disabled={isCheckingPermissions}
              >
                <Text style={styles.btnText}>
                  {isCheckingPermissions ? "Checking..." : "Retry"}
                </Text>
              </TouchableOpacity>
            </>
          ) : isFetchingToken ? (
            <>
              <ActivityIndicator />
              <Text style={styles.centerText}>
                Fetching room credentials...
              </Text>
            </>
          ) : (
            <>
              <ActivityIndicator />
              <Text style={styles.centerText}>Connecting...</Text>
            </>
          )}
        </View>
      ) : (
        <>
          {isConversationPanelOpen ? (
            <View style={styles.tipsLayout}>
              <ScrollView
                contentContainerStyle={styles.gridRow}
                style={styles.videoRow}
                horizontal={true}
                showsHorizontalScrollIndicator={false}
              >
                <CallVideoTiles
                  variant="mobileCompact"
                  userName={userName}
                  remoteUsers={remoteUsers}
                  remoteNames={remoteNames}
                  localMicrophoneTrack={localMicrophoneTrack}
                  localCameraTrack={localCameraTrack}
                  micOn={micOn}
                  cameraOn={cameraOn}
                  onFlagUser={onFlagUser}
                />
              </ScrollView>
              <ConversationPanelMobile onClose={onCloseConversationPanel} />
            </View>
          ) : (
            <ScrollView
              contentContainerStyle={styles.gridColumn}
              style={styles.videoColumn}
              horizontal={false}
              showsVerticalScrollIndicator={false}
            >
              <CallVideoTiles
                variant="mobile"
                userName={userName}
                remoteUsers={remoteUsers}
                remoteNames={remoteNames}
                localMicrophoneTrack={localMicrophoneTrack}
                localCameraTrack={localCameraTrack}
                micOn={micOn}
                cameraOn={cameraOn}
                onFlagUser={onFlagUser}
              />
            </ScrollView>
          )}

          <View style={styles.controlsBar}>
            <View style={styles.controls}>
              <TouchableOpacity
                style={styles.controlItem}
                onPress={onToggleMic}
                accessibilityRole="button"
                accessibilityLabel={
                  micOn ? "Mute microphone" : "Unmute microphone"
                }
              >
                <View style={[styles.iconCtrl, !micOn && styles.iconCtrlOff]}>
                  <MicIcon width={30} height={30} off={!micOn} />
                </View>
                <Text style={styles.controlLabel}>Mic</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.controlItem}
                onPress={onToggleCamera}
                accessibilityRole="button"
                accessibilityLabel={
                  cameraOn ? "Turn camera off" : "Turn camera on"
                }
              >
                <View
                  style={[styles.iconCtrl, !cameraOn && styles.iconCtrlOff]}
                >
                  <CameraIcon width={24} height={24} off={!cameraOn} />
                </View>
                <Text style={styles.controlLabel}>Camera</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.controlItem}
                onPress={onToggleBlur}
              >
                <View style={styles.iconCtrl}>
                  <BlurIcon
                    width={28}
                    height={28}
                    style={!isBlurEnabled && styles.iconOff}
                  />
                </View>
                <Text style={styles.controlLabel}>Blur</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.controlItem}
                onPress={onToggleConversationPanel}
              >
                <View
                  style={[
                    styles.iconCtrl,
                    isConversationPanelOpen && styles.iconCtrlActive,
                  ]}
                >
                  <AdviceIcon width={28} height={28} />
                </View>
                <Text style={styles.controlLabel}>Tips</Text>
              </TouchableOpacity>
            </View>
          </View>
        </>
      )}
      <TimeWarningModal
        visible={isTimeWarningModalVisible}
        onClose={onCloseTimeWarning}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: "#232335",
    paddingHorizontal: 12,
    paddingBottom: 120,
  },
  center: { alignItems: "center", justifyContent: "center", padding: 24 },
  centerText: { color: "#c9cbdd", marginTop: 8, fontSize: 14 },
  gridColumn: {
    paddingVertical: 8,
    paddingBottom: 70,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 12,
    flexGrow: 1,
  },
  videoColumn: {
    flexGrow: 0,
  },
  tipsLayout: {
    gap: 12,
    paddingBottom: 70,
  },
  gridRow: {
    paddingVertical: 4,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 2,
  },
  videoRow: {
    width: "100%",
  },
  controlsBar: {
    position: "absolute",
    left: 12,
    right: 12,
    bottom: 18,
    backgroundColor: "#1C1D2F",
    borderRadius: 28,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  controls: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  controlItem: {
    alignItems: "center",
    gap: 6,
    width: 64,
  },
  iconCtrl: {
    width: 54,
    height: 54,
    backgroundColor: "#3A6FB1",
    borderRadius: 999,
    justifyContent: "center",
    alignItems: "center",
  },
  iconCtrlActive: {
    backgroundColor: "#D63C8A",
  },
  // Muted mic / camera off: red with a slashed icon, as in Zoom/Meet (#142).
  iconCtrlOff: {
    backgroundColor: "#E5484D",
  },
  iconOff: {
    opacity: 0.4,
  },
  controlLabel: {
    fontSize: 12,
    color: "#fff",
    fontWeight: "600",
  },
  errorText: {
    color: "#d32f2f",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  retry: {
    marginTop: 12,
    backgroundColor: "#1e88e5",
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  btnText: { color: "#fff", fontWeight: "700" },
});
